import { useContext, useEffect, useState } from "react";
import { Alert, Box, Chip, Typography } from "@mui/material";

import { isAdminEmail } from "@thalamos/aspire-common";

import { LoggedInUserContext } from "../../Contexts.js";
import { Demo } from "./ui/MhaStatusViz.js";

/**
 * Pathway Analytics — a deliberately distinct, chromeless workspace opened in
 * its own window from the Thalamos admin area (App.tsx routes it OUTSIDE the
 * usual <Layout>, so it renders with its own top bar and no aspire nav). It
 * reuses the aspire session cookie (same origin), so auth is transparent.
 *
 * Access is internal-only for now: the isAdminEmail (@thalamos.co.uk) gate here
 * is cosmetic — the aspire-server endpoints are the real enforcement (403). Both
 * swap for a trust-level RBAC permission later.
 *
 * The body is the ported pathway-visualiser (Demo), fed on demand from the
 * pathway tables via the filtered facets + patient-rows endpoints.
 */

const ACCENT = "#0f4c5c";
const HEADER_HEIGHT = 48;

export function PathwayWorkspace() {
  const userContext = useContext(LoggedInUserContext);
  const email = userContext?.user?.email;
  const authorised = email !== undefined && isAdminEmail(email);

  // The visualiser lays its grid out from explicit pixel dimensions; track the
  // window so it reflows on resize.
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: Math.max(0, window.innerHeight - HEADER_HEIGHT),
  });
  useEffect(() => {
    const onResize = () => {
      setSize({
        width: window.innerWidth,
        height: Math.max(0, window.innerHeight - HEADER_HEIGHT),
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f4f6f8",
      }}
    >
      <Box
        component="header"
        sx={{
          height: HEADER_HEIGHT,
          flexShrink: 0,
          bgcolor: ACCENT,
          color: "#fff",
          px: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Pathway Analytics
        </Typography>
        <Chip
          label="Thalamos internal"
          size="small"
          sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff" }}
        />
      </Box>

      <Box component="main" sx={{ flexGrow: 1, minHeight: 0 }}>
        {!authorised ? (
          <Alert severity="error" sx={{ m: 3 }}>
            Pathway analytics is restricted to Thalamos staff.
          </Alert>
        ) : (
          <Demo width={size.width} height={size.height} />
        )}
      </Box>
    </Box>
  );
}
